import { Box, Link, Text, VStack } from '@chakra-ui/react'
import {
  AlmaIcon,
  GCalIcon,
  HeadwayIcon,
  ICalIcon,
  OutlookIcon,
  SimplePracticeIcon,
  TherapyNotesIcon
} from '@icons'
import React, { useCallback } from 'react'
import { useClinicianSchedulingControllerGetCalendars } from '~/clinician-api'
import type { CalendarResponseDto } from '~/clinician-api/models'
import { CalendarListItem } from './calendar-list-item'

interface MyCalendarsProps {
  enabledCalendars: Record<string, boolean>
  setEnabledCalendars: (calendars: Record<string, boolean>) => void
}

export const MyCalendars: React.FC<MyCalendarsProps> = ({
  enabledCalendars,
  setEnabledCalendars
}) => {
  const { data: calendars = [] } =
    useClinicianSchedulingControllerGetCalendars()

  const calendarOptions = [
    {
      icon: <GCalIcon width={16} height={16} />,
      title: 'Google Calendar',
      type: 'google'
    },
    {
      icon: <OutlookIcon width={16} height={14} />,
      title: 'Outlook',
      type: 'outlook'
    },
    {
      icon: <ICalIcon width={16} height={16} />,
      title: 'iCal',
      type: 'ical'
    },
    {
      icon: <SimplePracticeIcon width={16} height={16} />,
      title: 'Simple Practice',
      type: 'simplepractice'
    },
    {
      icon: <HeadwayIcon width={16} height={16} />,
      title: 'Headway',
      type: 'headway'
    },
    {
      icon: <AlmaIcon width={16} height={16} />,
      title: 'Alma',
      type: 'alma'
    },
    {
      icon: <TherapyNotesIcon width={16} height={16} />,
      title: 'TherapyNotes',
      type: 'therapynotes'
    }
  ]

  const handleCalendarToggle = useCallback(
    (calendarId: string, enabled: boolean) => {
      const newEnabled = { ...enabledCalendars, [calendarId]: enabled }
      setEnabledCalendars(newEnabled)
    },
    [enabledCalendars, setEnabledCalendars]
  )

  const getCalendarIcon = (type: string) => {
    if (type === 'blueprint') {
      return null
    }
    const option = calendarOptions.find(opt => opt.type === type)
    return option?.icon || <ICalIcon width={16} height={16} />
  }

  const getCalendarTitle = (type: string): string => {
    if (type === 'blueprint') {
      return 'Blueprint Calendar'
    }
    const option = calendarOptions.find(opt => opt.type === type)
    return option?.title || type
  }

  return (
    <Box width="100%">
      <Text fontSize="12px" mb="4px">
        My Calendars
      </Text>

      <VStack align="start" spacing="4px" mb="8px">
        {calendars.map((calendar: CalendarResponseDto) => (
          <CalendarListItem
            key={calendar.systemId}
            id={calendar.systemId}
            name={calendar.name || getCalendarTitle(calendar.type)}
            type={calendar.type}
            isEnabled={enabledCalendars[calendar.systemId] ?? true}
            onToggle={handleCalendarToggle}
            getIcon={getCalendarIcon}
          />
        ))}
      </VStack>

      <Link
        fontSize="16px"
        color="#2D54E8"
        fontWeight="500"
        ml="8px"
        href="/settings/calendar-sync"
        target="_blank"
      >
        + Add calendar
      </Link>
    </Box>
  )
}
